import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';

import { I18n } from '@aws-amplify/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { enableMapSet } from 'immer';
import { Observable, Subscription } from 'rxjs';

import { UserDetails } from '@authentication/interfaces/user';
import { UserService } from '@authentication/services/user.service';
import { AuthenticationSelectors } from '@authentication/store/authentication.slice';
import { environment } from '@environments/environment';
import {
  MerchantActions,
  MerchantSelectors,
} from '@merchant/store/merchant.slice';
import { ScriptService } from '@shared/services/script.service';

import { MerchantBasicDataViewModel } from './api/models';
import { ImpersonationActions } from './easylot-admin/modules/impersonation/store/impersonation.slice';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private merchantBasicData$: Observable<
    MerchantBasicDataViewModel | undefined
  > = this.store.select(MerchantSelectors.selectBasicData);
  private subscription = new Subscription();
  currentUser: UserDetails | null = null;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private userService: UserService,
    private store: Store,
    public translate: TranslateService,
    private router: Router,
    private readonly renderer: Renderer2,
    private readonly scriptService: ScriptService
  ) {
    translate.setDefaultLang('hu');
    I18n.setLanguage('hu');
    enableMapSet();

    this.subscription.add(
      this.userService.getCurrentSession().subscribe((session) => {
        if (session.isValid()) {
          this.userService.getUserAsync().then((user) => {
            if (this.userService.currentUser.value?.email !== user.email) {
              this.userService.currentUser.next(user);
              this.userService.currentUserRoles.next(user.roles);
            }
          });
        }
      })
    );

    this.subscription.add(
      this.store
        .select(AuthenticationSelectors.selectUserInfo)
        .subscribe((userInfo) => {
          this.userService.getUserAsync().then((user) => {
            if (
              this.currentUser &&
              this.currentUser.impersonated !== user.impersonated
            ) {
              this.userService.currentUser.next(user);
              this.userService.currentUserRoles.next(user.roles);
              if (user.impersonated) {
                if (this.router.url.startsWith('/admin')) {
                  this.router.navigate(['/home']);
                }
              } else {
                this.router.navigate(['/admin/impersonation']);
              }
            }
          });
        })
    );

    this.subscription.add(
      this.userService.currentUser.subscribe((currentUser) => {
        if (
          currentUser &&
          (currentUser.email !== this.currentUser?.email ||
            currentUser.impersonated !== this.currentUser?.impersonated)
        ) {
          if (
            !currentUser.roles.includes('EASYLOT_ADMIN') &&
            !currentUser.roles.includes('TELESALES') &&
            !currentUser.roles.includes('IMPERSONATION')
          ) {
            this.store.dispatch(MerchantActions.loadBasicData.trigger());
          }
        }
        this.currentUser = currentUser;
      })
    );

    this.subscription.add(
      this.merchantBasicData$.subscribe((data) => {
        this.document.documentElement.className = '';
        this.document.documentElement.classList.add('theme-easylot');
        // on first login navigate to basic-data
        if (data && !data?.businessAddress?.zipCode) {
          if (!this.userService.currentUserRoles.value?.includes('TELESALES')) {
            this.router.navigate(['/account/merchant/basic-data']);
          }
        }
      })
    );
  }

  ngOnInit(): void {
    if (environment.optimonkAccountId) {
      this.scriptService.loadJsScript(
        this.renderer,
        `https://onsite.optimonk.com/script.js?account=${environment.optimonkAccountId}`
      );
    }
  }

  removeImpersonation() {
    this.store.dispatch(ImpersonationActions.removeImpersonation.trigger({}));
    this.userService.resetStores();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
